// Plattform-Farben und Icons
const PLATFORM_STYLES = {
  Twitch: { color: '#9147ff', icon: '🎮' },
  YouTube: { color: '#ff0000', icon: '▶️' },
  Kick: { color: '#53fc18', icon: '🟢' },
  Trovo: { color: '#00e1c6', icon: '💎' },
  TikTok: { color: '#69C9D0', icon: '🎵' },
  Facebook: { color: '#1877F2', icon: '📘' },
  Instagram: { color: '#E1306C', icon: '📷' },
  DLive: { color: '#FFD300', icon: '🟡' },
  NimoTV: { color: '#3E82F7', icon: '📺' },
  Rumble: { color: '#85C742', icon: '▶️' },
  LinkedIn: { color: '#0A66C2', icon: '💼' },
  Twitter: { color: '#000000', icon: '✖️' },
  X: { color: '#000000', icon: '✖️' },
  Vimeo: { color: '#19B7EA', icon: '▶️' },
};

import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../api";
import { UserContext } from "../context/UserContext";
import { resolveMediaUrl } from "../utils/url";

function isSameLocalDate(a, b) {
  if (!(a instanceof Date) || !(b instanceof Date)) return false;
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

const DAYS = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];
const SLOTS = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, "0");
  const m = i % 2 === 0 ? "00" : "30";
  return `${h}:${m}`;
});

// === Layout-Konstanten ===
const ROW_HEIGHT = 24;            // px pro 30-Minuten-Slot
const PX_PER_MIN = ROW_HEIGHT / 30;
const GAP = 4;                    // px Abstand zwischen parallelen Events

// Ermittelt die Plattform anhand eines Links (Hostname)
function detectServiceFromUrl(url) {
  if (!url || typeof url !== 'string') return null;
  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`);
    const host = u.hostname.toLowerCase();
    if (host.includes('twitch')) return 'Twitch';
    if (host.includes('youtube') || host.includes('youtu.be')) return 'YouTube';
    if (host.includes('kick')) return 'Kick';
    if (host.includes('trovo')) return 'Trovo';
    if (host.includes('tiktok')) return 'TikTok';
    if (host.includes('facebook') || host.includes('fb.gg') || host.includes('fb.watch')) return 'Facebook';
    if (host.includes('instagram')) return 'Instagram';
    if (host.includes('dlive')) return 'DLive';
    if (host.includes('nimo')) return 'NimoTV';
    if (host.includes('rumble')) return 'Rumble';
    if (host.includes('linkedin')) return 'LinkedIn';
    if (host.includes('twitter') || host === 'x.com' || host.endsWith('.x.com')) return 'X';
    if (host.includes('vimeo')) return 'Vimeo';
    return null;
  } catch {
    return null;
  }
}

function detectServiceFallback(platformText) {
  if (!platformText) return null;
  const p = String(platformText).toLowerCase();
  if (p.includes('twitch')) return 'Twitch';
  if (p.includes('youtube')) return 'YouTube';
  if (p.includes('kick')) return 'Kick';
  if (p.includes('trovo')) return 'Trovo';
  if (p.includes('tiktok')) return 'TikTok';
  if (p.includes('facebook') || p.includes('fb.gg') || p.includes('fb ')) return 'Facebook';
  if (p.includes('instagram')) return 'Instagram';
  if (p.includes('dlive')) return 'DLive';
  if (p.includes('nimo')) return 'NimoTV';
  if (p.includes('rumble')) return 'Rumble';
  if (p.includes('linkedin')) return 'LinkedIn';
  if (p.includes('twitter') || p === 'x') return 'X';
  if (p.includes('vimeo')) return 'Vimeo';
  return null;
}

function getHostname(url) {
  if (!url || typeof url !== 'string') return null;
  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`);
    return (u.hostname || '').toLowerCase();
  } catch {
    return null;
  }
}

// Hilfsfunktionen für Datums-/Zeit-Inputs (lokal)
function formatLocalDateInput(d) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
function toLocalInputValue(iso) {
  const d = new Date(iso);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}
function fromLocalInputValue(val) {
  // val = 'YYYY-MM-DDTHH:mm' (lokal)
  return new Date(val).toISOString();
}

// Liefert dayStart/dayEnd (LOKALE Zeit) für dayIdx (0=Mo..6=So)
function getDayBounds(weekStart, dayIdx) {
  const dayStart = new Date(weekStart);
  dayStart.setHours(0, 0, 0, 0);
  dayStart.setDate(dayStart.getDate() + dayIdx);
  const dayEnd = new Date(dayStart);
  dayEnd.setDate(dayEnd.getDate() + 1);
  return { dayStart, dayEnd };
}

// Schneidet Events auf den Tagesbereich zu und bereitet Minuten/Pixel vor
function buildDayEvents(entries, weekStart, dayIdx) {
  const { dayStart, dayEnd } = getDayBounds(weekStart, dayIdx);
  const result = [];

  for (const e of entries) {
    const s = new Date(e.start);
    const en = new Date(e.end);
    // nur Events, die den Tag berühren
    if (en <= dayStart || s >= dayEnd) continue;

    // auf den Tag clippen (für Darstellung)
    const clippedStart = s < dayStart ? dayStart : s;
    const clippedEnd = en > dayEnd ? dayEnd : en;

    const minutesFromDayStart = Math.max(0, Math.round((clippedStart.getTime() - dayStart.getTime()) / 60000));
    const minutesToEnd = Math.max(1, Math.round((clippedEnd.getTime() - dayStart.getTime()) / 60000));
    const durationMin = Math.max(1, minutesToEnd - minutesFromDayStart);

    result.push({
      ...e,
      _startMin: minutesFromDayStart,
      _durMin: durationMin,
      _origStart: s.getTime(),
      _origEnd: en.getTime(),
    });
  }
  return result;
}

// Weist parallelen Events Spalten-Indizes zu (Interval Partitioning)
function assignColumns(dayEvents) {
  const evts = [...dayEvents].sort(
    (a, b) => a._origStart - b._origStart || a._origEnd - b._origEnd
  );

  let active = []; // { evt, col }
  let nextClusterId = 0;
  let clusterId = -1;
  const takenCols = new Set();
  const byCluster = new Map(); // clusterId -> { maxCols }

  for (const evt of evts) {
    // inaktive entfernen
    active = active.filter((x) => {
      if (x.evt._origEnd <= evt._origStart) {
        takenCols.delete(x.col);
        return false;
      }
      return true;
    });

    // neuen Cluster starten, wenn keiner aktiv
    if (active.length === 0) {
      clusterId = nextClusterId++;
      byCluster.set(clusterId, { maxCols: 0 });
      takenCols.clear();
    }

    // kleinste freie Spalte finden
    let col = 0;
    while (takenCols.has(col)) col++;
    takenCols.add(col);

    active.push({ evt, col });
    evt._col = col;
    evt._cluster = clusterId;

    const info = byCluster.get(clusterId);
    info.maxCols = Math.max(info.maxCols, col + 1);
  }

  // Cluster-Größen zurückschreiben
  for (const evt of evts) {
    const info = byCluster.get(evt._cluster);
    evt._clusterSize = info.maxCols;
  }
  return evts;
}

/** =========================
 *  Modal als eigene Komponente (Props)
 *  ========================= */
function EntryModal({ modal, weekStart, user, onClose, onSave, onDelete, platformIcons, platformIconsByHost }) {
  if (modal == null) return null;

  const { entry, dayIdx, slotIdx } = modal;

  let slotStart, slotEnd;
  if (typeof dayIdx === "number" && typeof slotIdx === "number") {
    slotStart = new Date(weekStart);
    slotStart.setHours(0, 0, 0, 0);
    slotStart.setDate(slotStart.getDate() + dayIdx);
    slotStart.setMinutes(slotIdx * 30);
    slotEnd = new Date(slotStart);
    slotEnd.setMinutes(slotEnd.getMinutes() + 30);
  } else {
    // Default: Montag 12:00 - 12:30 LOKAL der ausgewählten Woche
    slotStart = new Date(weekStart);
    slotStart.setHours(12, 0, 0, 0);
    slotEnd = new Date(slotStart);
    slotEnd.setMinutes(slotEnd.getMinutes() + 30);
    if (isNaN(slotStart.getTime())) {
      slotStart = new Date();
      slotStart.setHours(12, 0, 0, 0);
      slotEnd = new Date(slotStart);
      slotEnd.setMinutes(slotEnd.getMinutes() + 30);
    }
  }

  // Social-Links sicher bestimmen (unterstützt Array- und Objekt-Formate)
  const rawLinks = user?.socialLinks;
  function deriveKeyFromUrl(url) {
    if (!url || typeof url !== 'string') return '';
    try {
      const u = new URL(url);
      const host = u.hostname || '';
      const h = host.toLowerCase();
      if (h.includes('twitch')) return 'Twitch';
      if (h.includes('youtube') || h.includes('youtu.be')) return 'YouTube';
      if (h.includes('kick')) return 'Kick';
      if (h.includes('trovo')) return 'Trovo';
      if (h.includes('tiktok')) return 'TikTok';
      if (h.includes('facebook') || h.includes('fb.gg') || h.includes('fb.watch')) return 'Facebook';
      if (h.includes('instagram')) return 'Instagram';
      if (h.includes('dlive')) return 'DLive';
      if (h.includes('nimo')) return 'NimoTV';
      if (h.includes('rumble')) return 'Rumble';
      if (h.includes('linkedin')) return 'LinkedIn';
      if (h.includes('twitter') || h === 'x.com') return 'X';
      if (h.includes('vimeo')) return 'Vimeo';
      return host.replace('www.', '') || url;
    } catch {
      return url;
    }
  }
  let socialEntries = [];
  if (Array.isArray(rawLinks)) {
    socialEntries = rawLinks
      .filter((l) => l && (l.name || l.url))
      .map((l, idx) => ({
        key: (l.name && String(l.name).trim()) || deriveKeyFromUrl(l.url) || `Link ${idx + 1}`,
        url: l.url || "",
      }));
  } else if (rawLinks && typeof rawLinks === 'object') {
    socialEntries = Object.entries(rawLinks).map(([k, v]) => ({
      key: String(k),
      url: typeof v === 'string' ? v : "",
    }));
  }

  // Fallback-Plattformen, falls keine Social-Links vorhanden
  const fallbackPlatforms = [
    "Twitch", "YouTube", "Kick", "Trovo",
    "TikTok", "Facebook", "Instagram", "DLive", "NimoTV", "Rumble", "LinkedIn", "X", "Vimeo"
  ];

  // Robuster Default für platform
  const twitchEntry = socialEntries.find((e) => e.key === 'Twitch');
  const defaultPlatform = entry?.platform
    ?? (twitchEntry?.key || socialEntries[0]?.key || fallbackPlatforms[0] || "Sonstige...");

  const [customLink, setCustomLink] = useState("");
  const [form, setForm] = useState(
    entry
      ? { ...entry }
      : {
          start: slotStart.toISOString(),
          end: slotEnd.toISOString(),
          platform: defaultPlatform,
          title: "",
          description: "",
        }
  );

  const isOwner = !entry || entry.userId === user?.id || user?.permissions?.includes("supportplan.manageAll");
  const isPast = new Date(form.end) < new Date();
  const canEditPast = user?.permissions?.includes("supportplan.editPast");
  if (entry && isPast && !canEditPast) return null;

  // Plattform/Link für Anzeige-Icon bestimmen (live bei Auswahl/Custom-Link)
  const selectedSocial = socialEntries.find((s) => s.key === form.platform);
  const candidateLink = form.platform === "Sonstige..." ? customLink : (selectedSocial?.url || "");
  const detectedService = detectServiceFromUrl(candidateLink);
  const service = detectedService || detectServiceFallback(form.platform) || form.platform;
  const platformStyle = PLATFORM_STYLES[service] || { color: '#32CD89', icon: '' };
  const host = getHostname(candidateLink);
  const iconUrl = (service && platformIcons?.[service]) || (host && (platformIconsByHost?.[host] || platformIconsByHost?.[host?.replace(/^www\./,'')])) || null;

  return (
    <div
      className="modal-bg"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={(e) => {
        // Klick auf den halbtransparenten Hintergrund schließt Modal
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal-content"
        style={{
          background: "#222",
          padding: 32,
          borderRadius: 16,
          minWidth: 340,
          maxWidth: 420,
        }}
      >
        {(() => {
          const authorAvatarUrl = resolveMediaUrl(
            (entry?.avatarUrl && entry.avatarUrl.length > 0)
              ? entry.avatarUrl
              : (user?.avatarUrl && user.avatarUrl.length > 0)
                ? user.avatarUrl
                : '/avatars/default-avatar.png'
          );
          const authorName = entry?.userDisplayName || user?.displayName || user?.username || "";
          return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
              <h2 style={{ margin: 0 }}>{entry ? "Eintrag bearbeiten" : "Stream eintragen"}</h2>
              <img
                src={authorAvatarUrl}
                alt={authorName || "Avatar"}
                title={authorName}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "1px solid #444",
                  background: "#222",
                }}
              />
            </div>
          );
        })()}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            const submitData = { ...form };
            const selected = socialEntries.find((s) => s.key === form.platform);
            // Wenn Sonstige... gewählt, Link übernehmen, sonst aus Social-Links (oder leer)
            submitData.streamLink = form.platform === "Sonstige..." ? customLink : (selected?.url || "");
            onSave(submitData);
          }}
          style={{ display: "flex", flexDirection: "column", gap: 12 }}
        >
          <label>
            Plattform/Link
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              {iconUrl ? (
                <span
                  style={{
                    width: 20,
                    height: 20,
                    minWidth: 20,
                    minHeight: 20,
                    borderRadius: '50%',
                    background: '#fff',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.1)'
                  }}
                  title={service}
                >
                  <img src={iconUrl} alt={service} style={{ width: 14, height: 14, objectFit: 'contain' }} />
                </span>
              ) : (
                <span style={{ fontSize: 16 }} title={service}>{platformStyle.icon}</span>
              )}
              <select
              value={form.platform}
              onChange={(e) => {
                const val = e.target.value;
                setForm((f) => ({ ...f, platform: val }));
                if (val === "Sonstige...") setCustomLink("");
              }}
              >
              {socialEntries.map((e) => (
                <option key={e.key} value={e.key}>
                  {e.key} {e.url ? `(${e.url})` : ""}
                </option>
              ))}

              {/* Falls keine Social-Links vorhanden, biete die bekannten Plattformen an */}
              {socialEntries.length === 0 &&
                fallbackPlatforms.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}

              <option value="Sonstige...">Sonstige...</option>
            </select>
            </div>
          </label>

          {form.platform === "Sonstige..." && (
            <label>
              Stream-Link
              <input
                type="text"
                value={customLink}
                onChange={(e) => setCustomLink(e.target.value)}
                placeholder="Link zum Stream eintragen"
                required
              />
            </label>
          )}

          <label>
            Titel
            <input
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              required
            />
          </label>

          <label>
            Beschreibung
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </label>

          <label>
            Start
            <input
              type="datetime-local"
              value={toLocalInputValue(form.start)}
              onChange={(e) =>
                setForm((f) => ({ ...f, start: fromLocalInputValue(e.target.value) }))
              }
              required
            />
          </label>

          <label>
            Ende
            <input
              type="datetime-local"
              value={toLocalInputValue(form.end)}
              onChange={(e) =>
                setForm((f) => ({ ...f, end: fromLocalInputValue(e.target.value) }))
              }
              required
            />
          </label>

          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => {
                // Öffnet den Stream-Link in neuem Tab
                // Quelle: ausgewählte Plattform-URL oder Custom-Link
                const rawLinks = user?.socialLinks;
                let socialEntries = [];
                if (Array.isArray(rawLinks)) {
                  socialEntries = rawLinks
                    .filter((l) => l && (l.name || l.url))
                    .map((l, idx) => ({
                      key: (l.name && String(l.name).trim()) || (l.url || `Link ${idx + 1}`),
                      url: l.url || "",
                    }));
                } else if (rawLinks && typeof rawLinks === 'object') {
                  socialEntries = Object.entries(rawLinks).map(([k, v]) => ({
                    key: String(k),
                    url: typeof v === 'string' ? v : "",
                  }));
                }
                const selected = socialEntries.find((s) => s.key === form.platform);
                const url = form.platform === "Sonstige..." ? (customLink || "") : (selected?.url || "");
                if (!url) return;
                const finalUrl = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
                window.open(finalUrl, '_blank', 'noopener,noreferrer');
              }}
              style={{
                background: "#3B82F6", // blau
                color: "#fff",
                padding: "8px 16px",
                borderRadius: 6,
                border: "none",
                fontWeight: 600,
              }}
            >
              Stream öffnen
            </button>

            <button
              type="submit"
              style={{
                background: "#32CD89",
                color: "#222",
                padding: "8px 16px",
                borderRadius: 6,
                border: "none",
                fontWeight: 600,
              }}
            >
              {entry ? "Speichern" : "Eintragen"}
            </button>

            <button
              type="button"
              onClick={onClose}
              style={{
                background: "#888",
                color: "#222",
                padding: "8px 16px",
                borderRadius: 6,
                border: "none",
              }}
            >
              Abbrechen
            </button>

            {entry && isOwner && (
              <button
                type="button"
                onClick={() => {
                  if (confirm("Diesen Eintrag wirklich löschen?")) {
                    onDelete(entry.id);
                  }
                }}
                style={{
                  background: "#e74c3c",
                  color: "#fff",
                  padding: "8px 16px",
                  borderRadius: 6,
                  border: "none",
                }}
              >
                Löschen
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

/** =========================
 *  Read-only Modal (fremde Einträge)
 *  ========================= */
function EntryViewModal({ modal, onClose, onOpenProfile, platformIcons, platformIconsByHost }) {
  if (modal == null) return null;

  const entry = modal.entry;
  if (!entry) return null;

  const authorAvatarUrl = resolveMediaUrl(
    (entry?.avatarUrl && entry.avatarUrl.length > 0)
      ? entry.avatarUrl
      : '/avatars/default-avatar.png'
  );
  const authorName = entry?.userDisplayName || (entry?.userId != null ? `User ${entry.userId}` : "");

  const detected = detectServiceFromUrl(entry.streamLink);
  const service = detected || detectServiceFallback(entry.platform) || entry.platform;
  const platformStyle = PLATFORM_STYLES[service] || { color: '#32CD89', icon: '' };
  const host = getHostname(entry.streamLink);
  const iconUrl = (service && platformIcons?.[service]) || (host && (platformIconsByHost?.[host] || platformIconsByHost?.[host?.replace(/^www\./,'')])) || null;

  const start = entry.start ? new Date(entry.start) : null;
  const end = entry.end ? new Date(entry.end) : null;
  const hasValidTimes = start instanceof Date && !isNaN(start) && end instanceof Date && !isNaN(end);

  const isFuture = hasValidTimes && start.getTime() > Date.now();

  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [reminderLoaded, setReminderLoaded] = useState(false);
  const [reminderSaving, setReminderSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    setReminderLoaded(false);
    if (!entry?.id) return () => {};
    api.get(`/supportplan/entry/${entry.id}/reminder`)
      .then((res) => {
        if (!mounted) return;
        setReminderEnabled(!!res?.data?.enabled);
        setReminderLoaded(true);
      })
      .catch(() => {
        if (!mounted) return;
        setReminderLoaded(true);
      });
    return () => { mounted = false; };
  }, [entry?.id]);

  const canOpenStream = typeof entry.streamLink === 'string' && entry.streamLink.trim().length > 0;

  return (
    <div
      className="modal-bg"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal-content"
        style={{
          background: "#222",
          padding: 32,
          borderRadius: 16,
          minWidth: 340,
          maxWidth: 520,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <img
              src={authorAvatarUrl}
              alt={authorName || "Avatar"}
              title={authorName}
              style={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                objectFit: "cover",
                border: "1px solid #444",
                background: "#222",
                flex: '0 0 auto',
              }}
            />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, opacity: 0.75 }}>Streamplan-Eintrag von</div>
              <div style={{ fontSize: 16, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {authorName}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '0 0 auto' }}>
            {iconUrl ? (
              <span
                style={{
                  width: 22,
                  height: 22,
                  minWidth: 22,
                  minHeight: 22,
                  borderRadius: '50%',
                  background: '#fff',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 0 0 1px rgba(0,0,0,0.1)'
                }}
                title={service}
              >
                <img src={iconUrl} alt={service} style={{ width: 14, height: 14, objectFit: 'contain' }} />
              </span>
            ) : (
              <span style={{ fontSize: 18 }} title={service}>{platformStyle.icon}</span>
            )}
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                padding: '4px 8px',
                borderRadius: 999,
                background: platformStyle.color,
                color: '#222',
              }}
              title={entry.platform || service}
            >
              {service || entry.platform || 'Plattform'}
            </span>
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>{entry.title || 'Ohne Titel'}</div>
          {hasValidTimes && (
            <div style={{ fontSize: 13, opacity: 0.8, marginTop: 6 }}>
              {start.toLocaleDateString('de-DE', { weekday: 'long', year: 'numeric', month: '2-digit', day: '2-digit' })}
              {' · '}
              {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              {' – '}
              {end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          )}
        </div>

        {entry.description ? (
          <div
            style={{
              background: '#1a1a1a',
              border: '1px solid #2a2a2a',
              borderRadius: 12,
              padding: 14,
              marginBottom: 16,
              whiteSpace: 'pre-wrap',
              lineHeight: 1.35,
            }}
          >
            {entry.description}
          </div>
        ) : (
          <div style={{ fontSize: 13, opacity: 0.65, marginBottom: 16 }}>Keine Beschreibung vorhanden.</div>
        )}

        <div style={{ marginBottom: 16 }}>
          {isFuture ? (
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: reminderSaving ? 'not-allowed' : 'pointer', opacity: reminderSaving ? 0.7 : 1 }}>
              <input
                type="checkbox"
                checked={!!reminderEnabled}
                disabled={!reminderLoaded || reminderSaving}
                onChange={async () => {
                  if (!entry?.id) return;
                  const next = !reminderEnabled;
                  setReminderSaving(true);
                  try {
                    await api.post(`/supportplan/entry/${entry.id}/reminder`, { enabled: next });
                    setReminderEnabled(next);
                  } catch (e) {
                    alert(e?.response?.data?.error || 'Erinnerung konnte nicht gespeichert werden.');
                  } finally {
                    setReminderSaving(false);
                  }
                }}
              />
              <div style={{ lineHeight: 1.2 }}>
                <div style={{ fontWeight: 700 }}>Reminder aktivieren</div>
                <div style={{ fontSize: 12, opacity: 0.75 }}>Zum Start bekommst du eine Benachrichtigung (wie bei Events).</div>
              </div>
            </label>
          ) : (
            <div style={{ fontSize: 12, opacity: 0.65 }}>Reminder sind nur für zukünftige Einträge verfügbar.</div>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
          {typeof entry.userId === 'number' && (
            <button
              type="button"
              onClick={() => {
                try { onOpenProfile?.(entry.userId); } catch {}
              }}
              style={{
                background: "#32CD89",
                color: "#222",
                padding: "10px 16px",
                borderRadius: 8,
                border: "none",
                fontWeight: 800,
              }}
            >
              Profil öffnen
            </button>
          )}

          {canOpenStream && (
            <button
              type="button"
              onClick={() => {
                const url = entry.streamLink || "";
                const finalUrl = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
                window.open(finalUrl, '_blank', 'noopener,noreferrer');
              }}
              style={{
                background: "#3B82F6",
                color: "#fff",
                padding: "10px 16px",
                borderRadius: 8,
                border: "none",
                fontWeight: 700,
              }}
            >
              Stream öffnen
            </button>
          )}

          <button
            type="button"
            onClick={onClose}
            style={{
              background: "#888",
              color: "#222",
              padding: "10px 16px",
              borderRadius: 8,
              border: "none",
              fontWeight: 700,
            }}
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SupportPlan() {
  const { user } = useContext(UserContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [platformIcons, setPlatformIcons] = useState({}); // name -> icon URL
  const [platformIconsByHost, setPlatformIconsByHost] = useState({}); // hostname -> icon URL

  // Woche initial lokal: Montag 00:00
  const [weekStart, setWeekStart] = useState(() => {
    const now = new Date();
    const day = now.getDay() === 0 ? 6 : now.getDay() - 1; // 0=So -> 6
    const monday = new Date(now);
    monday.setHours(0, 0, 0, 0);
    monday.setDate(monday.getDate() - day);
    return monday;
  });

  // Ansicht: Woche oder Tag
  const [viewMode, setViewMode] = useState('week'); // 'week' | 'day'

  const [modal, setModal] = useState(null); // {dayIdx, slotIdx, entry}
  const [viewModal, setViewModal] = useState(null); // {dayIdx, slotIdx, entry}
  const [customLink, setCustomLink] = useState(""); // bleibt, falls an anderer Stelle genutzt
  const [platforms] = useState(["Twitch", "YouTube", "Kick", "Trovo"]); // optionaler Fallback
  // Ansicht: Allgemein vs. Persönlich (User-Filter)
  const [viewScope, setViewScope] = useState('all'); // 'all' | 'personal'
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [allUsers, setAllUsers] = useState([]);

  // Deep-link support: parse query params to preselect view/user/week
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search || window.location.search || "");
      const qpView = params.get('view');
      const qpScope = params.get('scope');
      const qpUserId = params.get('userId');
      const qpWeekStart = params.get('weekStart') || params.get('date');

      if (qpView === 'day' || qpView === 'week') {
        setViewMode(qpView);
      }
      if (qpUserId) {
        setViewScope('personal');
        const uidNum = Number(qpUserId);
        if (!Number.isNaN(uidNum)) setSelectedUserId(uidNum);
      } else if (qpScope === 'personal' || qpScope === 'all') {
        setViewScope(qpScope);
      }
      if (qpWeekStart) {
        const d = new Date(qpWeekStart);
        if (!isNaN(d.getTime())) {
          // Align to Monday if in week mode; keep date as-is in day mode
          const local = new Date(d);
          local.setHours(0, 0, 0, 0);
          if ((qpView || viewMode) === 'week') {
            const day = local.getDay() === 0 ? 6 : local.getDay() - 1; // 0=So -> 6
            local.setDate(local.getDate() - day);
          }
          setWeekStart(local);
        }
      }
    } catch {}
    // only on first mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wenn auf persönliche Ansicht gewechselt wird, Standard: eigener User
  useEffect(() => {
    if (viewScope === 'personal' && (selectedUserId == null) && user?.id != null) {
      setSelectedUserId(user.id);
    }
  }, [viewScope, user?.id]);

  // Alle Nutzer für Dropdown laden (immer verfügbar, unabhängig von Einträgen)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get('/admin/user/list');
        if (!mounted) return;
        const items = Array.isArray(res.data) ? res.data : [];
        // Mappe Label-Feld, falls nötig
        const normalized = items.map(u => ({
          id: u.id,
          label: u.displayName || u.username || String(u.id),
          avatar: u.avatarUrl || '',
        })).sort((a, b) => a.label.localeCompare(b.label));
        setAllUsers(normalized);
      } catch (err) {
        console.warn('Konnte /admin/user/list nicht laden:', err?.response?.status || err?.message);
        // Fallback: zumindest eigener User
        if (mounted && user?.id) {
          setAllUsers([{ id: user.id, label: user.displayName || user.username || String(user.id), avatar: user.avatarUrl || '' }]);
        }
      }
    })();
    return () => { mounted = false; };
  }, [user?.id]);

  async function reloadEntries(targetWeekStart = weekStart) {
    const start = new Date(targetWeekStart);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    // In Tagesansicht nur 1 Tag laden, sonst 7 Tage
    end.setDate(end.getDate() + (viewMode === 'day' ? 1 : 7));
    const res = await api.get("/supportplan/entries", {
      params: {
        weekStart: start.toISOString(),
        weekEnd: end.toISOString(),
      },
    });
    setEntries(res.data);
  }

  useEffect(() => {
    reloadEntries().catch((err) => console.error("Supportplan: Laden fehlgeschlagen", err));
  }, [weekStart, viewMode]);

  // Social-Platform Icons laden
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get('/admin/social-platforms/list');
        if (!mounted) return;
        const byName = {};
        const byHost = {};
        for (const p of res.data || []) {
          if (!p || !p.icon) continue;
          let iconUrl = p.icon;
          if (iconUrl.startsWith('/')) {
            try { iconUrl = new URL(iconUrl, api.defaults.baseURL).toString(); } catch {}
          }
          if (p.name) byName[p.name] = iconUrl;
          if (p.urlPattern && typeof p.urlPattern === 'string') {
            try {
              const h = new URL(p.urlPattern).hostname.toLowerCase();
              const hNoWww = h.replace(/^www\./, '');
              byHost[h] = iconUrl;
              byHost[hNoWww] = iconUrl;
            } catch {
              // ignore invalid urlPattern
            }
          }
        }
        setPlatformIcons(byName);
        setPlatformIconsByHost(byHost);
      } catch (e) {
        console.warn('Konnte Social-Plattformen nicht laden:', e?.response?.status || e?.message);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Modal öffnen
  function openModal(dayIdx, slotIdx, entry) {
    // Leerer Slot / neuer Eintrag -> immer Edit-Modal
    if (!entry) {
      setViewModal(null);
      setModal({ dayIdx, slotIdx, entry });
      return;
    }

    const canManageAll = !!user?.permissions?.includes("supportplan.manageAll");
    const isOwner = entry.userId === user?.id || canManageAll;

    if (!isOwner) {
      setModal(null);
      setViewModal({ dayIdx, slotIdx, entry });
      return;
    }

    setViewModal(null);
    setModal({ dayIdx, slotIdx, entry });
  }

  // Eintrag speichern
  async function saveEntry(data) {
    try {
      if (data.id) {
        await api.patch(`/supportplan/entry/${data.id}`, data);
      } else {
        await api.post(`/supportplan/entry`, data);
      }
      await reloadEntries();
      setModal(null);
      alert("Eintrag gespeichert!");
    } catch (e) {
      alert(e?.response?.data?.error || "Fehler beim Speichern.");
    }
  }

  // Eintrag löschen
  async function deleteEntry(id) {
    try {
      await api.delete(`/supportplan/entry/${id}`);
      await reloadEntries();
      setModal(null);
      alert("Eintrag gelöscht.");
    } catch (e) {
      alert(e?.response?.data?.error || "Fehler beim Löschen.");
    }
  }

  // Wechsel der sichtbaren Range: in Wochenansicht +/- 7 Tage, in Tagesansicht +/- 1 Tag
  function changeWeek(offset) {
    const step = viewMode === 'week' ? 7 : 1;
    const newStart = new Date(weekStart);
    newStart.setDate(newStart.getDate() + offset * step);
    newStart.setHours(0, 0, 0, 0);
    setWeekStart(newStart);
  }

  const rangeLabel = viewMode === 'week'
    ? `${weekStart.toLocaleDateString()} - ${new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString()}`
    : `${weekStart.toLocaleDateString()}`;

  // Current-time indicator in the schedule grid (updates every minute)
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="home-page" style={{ width: "100vw", minHeight: "100vh", padding: "0 0 48px 0", position:'relative', left:'-120px' }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 16px 0 16px" }}>
        <h1 style={{ marginBottom: 18, textAlign: "center" }}>AP-N Streamplan</h1>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 18,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          {/* Ansicht-Umschalter */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={{ color: '#32CD89', fontWeight: 600 }}>Ansicht:</label>
            <button
              type="button"
              onClick={() => setViewMode('week')}
              style={{
                padding: '6px 12px', fontSize: 14, borderRadius: 6, border: '1px solid #ccc',
                background: viewMode === 'week' ? '#32CD89' : '#222', color: viewMode === 'week' ? '#222' : '#32CD89', cursor: 'pointer'
              }}
              title="Wochenansicht"
            >Woche</button>
            <button
              type="button"
              onClick={() => setViewMode('day')}
              style={{
                padding: '6px 12px', fontSize: 14, borderRadius: 6, border: '1px solid #ccc',
                background: viewMode === 'day' ? '#32CD89' : '#222', color: viewMode === 'day' ? '#222' : '#32CD89', cursor: 'pointer'
              }}
              title="Tagesansicht"
            >Tag</button>
          </div>
          <button
            onClick={() => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              if (viewMode === 'week') {
                // auf Montag der aktuellen Woche springen
                const day = today.getDay() === 0 ? 6 : today.getDay() - 1;
                today.setDate(today.getDate() - day);
              }
              setWeekStart(today);
            }}
            style={{
              padding: "6px 12px",
              fontSize: 15,
              borderRadius: 6,
              border: "1px solid #ccc",
              background: "#222",
              color: "#32CD89",
              cursor: "pointer",
            }}
            title={viewMode === 'week' ? 'Zur aktuellen Woche springen' : 'Zum heutigen Tag springen'}
          >
            Heute
          </button>

          <button
            onClick={() => changeWeek(-1)}
            style={{
              padding: "6px 12px",
              fontSize: 15,
              borderRadius: 6,
              border: "1px solid #ccc",
              background: "#222",
              color: "#32CD89",
              cursor: "pointer",
            }}
          >
            {viewMode === 'week' ? '← Vorwoche' : '← Vortag'}
          </button>

          <span style={{ fontWeight: 600, fontSize: 18 }}>{rangeLabel}</span>

          <button
            onClick={() => changeWeek(1)}
            style={{
              padding: "6px 12px",
              fontSize: 15,
              borderRadius: 6,
              border: "1px solid #ccc",
              background: "#222",
              color: "#32CD89",
              cursor: "pointer",
            }}
          >
            {viewMode === 'week' ? 'Nächste Woche →' : 'Nächster Tag →'}
          </button>

          <input
            type="date"
            value={formatLocalDateInput(weekStart)}
            onChange={(e) => {
              const [y, m, d] = e.target.value.split("-").map((v) => parseInt(v, 10));
              const selected = new Date(y, m - 1, d);
              selected.setHours(0, 0, 0, 0);
              if (viewMode === 'week') {
                // Montag dieser Woche (lokal)
                const day = selected.getDay() === 0 ? 6 : selected.getDay() - 1;
                selected.setDate(selected.getDate() - day);
              }
              setWeekStart(selected);
            }}
            style={{
              padding: "6px 12px",
              fontSize: 15,
              borderRadius: 6,
              border: "1px solid #ccc",
              background: "#222",
              color: "#32CD89",
              cursor: "pointer",
            }}
            title={viewMode === 'week' ? 'Zu einer bestimmten Woche springen' : 'Zu einem bestimmten Tag springen'}
          />
        </div>

        <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ color: '#32CD89', fontWeight: 600 }}>Ansicht:</label>
            <button
              type="button"
              onClick={() => setViewScope('all')}
              style={{
                padding: '6px 12px', fontSize: 14, borderRadius: 6, border: '1px solid #ccc',
                background: viewScope === 'all' ? '#32CD89' : '#222', color: viewScope === 'all' ? '#222' : '#32CD89', cursor: 'pointer'
              }}
              title="Allgemeine Ansicht"
            >Allgemein</button>
            <button
              type="button"
              onClick={() => setViewScope('personal')}
              style={{
                padding: '6px 12px', fontSize: 14, borderRadius: 6, border: '1px solid #ccc',
                background: viewScope === 'personal' ? '#32CD89' : '#222', color: viewScope === 'personal' ? '#222' : '#32CD89', cursor: 'pointer'
              }}
              title="Persönliche Ansicht"
            >Persönlich</button>

            {viewScope === 'personal' && (
              <select
                value={selectedUserId ?? user?.id ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedUserId(val === '' ? null : Number(val));
                }}
                style={{
                  padding: '6px 12px', fontSize: 14, borderRadius: 6, border: '1px solid #ccc',
                  background: '#222', color: '#32CD89', cursor: 'pointer'
                }}
                title="Benutzer wählen"
              >
                {user && (
                  <option value={user.id}>Eigene Einträge ({user.displayName || user.username || user.id})</option>
                )}
                {allUsers
                  .filter(uo => !user || uo.id !== user.id)
                  .map(uo => (
                    <option key={uo.id} value={uo.id}>{uo.label}</option>
                  ))}
              </select>
            )}
          </div>

          <button
            onClick={() => openModal(null, null, null)}
            style={{
              background: "#32CD89",
              color: "#222",
              borderRadius: 8,
              padding: "8px 18px",
              fontSize: 17,
              fontWeight: 600,
              border: "none",
              boxShadow: "0 2px 8px #0002",
              cursor: "pointer",
            }}
          >
            + Neuer Eintrag
          </button>
        </div>

        {/* Neuer Layout-Container */}
        <div
          style={{
            overflowX: "auto",
            background: "#181818",
            borderRadius: 12,
            padding: 8,
            boxShadow: "0 2px 16px #0002",
          }}
        >
          {/* Kopfzeile: Wochentage + Datum (abhängig von viewMode) */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `60px repeat(${viewMode === 'week' ? 7 : 1}, 1fr)`,
              columnGap: 8,
              alignItems: "stretch",
            }}
          >
            <div />
            {(viewMode === 'week' ? DAYS : [DAYS[(new Date(weekStart).getDay() + 6) % 7]]).map((day, i) => (
              <div
                key={"h-" + i}
                style={{
                  background: "#222",
                  color: "#32CD89",
                  padding: 8,
                  borderRadius: 8,
                  textAlign: "center",
                  fontWeight: 700,
                }}
              >
                {day}
              </div>
            ))}

            <div />
            {(viewMode === 'week' ? DAYS.map((_, i) => i) : [0]).map((i) => {
              const d = new Date(weekStart);
              d.setDate(d.getDate() + i);
              return (
                <div
                  key={"d-" + i}
                  style={{
                    background: "#222",
                    color: "#32CD89",
                    padding: "4px 8px",
                    borderRadius: 8,
                    textAlign: "center",
                    fontSize: 13,
                  }}
                >
                  {d.toLocaleDateString()}
                </div>
              );
            })}
          </div>

          {/* Body: Zeitspalte + Tages-Spalten (abhängig von viewMode) */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `60px repeat(${viewMode === 'week' ? 7 : 1}, 1fr)`,
              columnGap: 8,
              marginTop: 8,
            }}
          >
            {/* Zeitspalte */}
            <div style={{ position: "relative", height: ROW_HEIGHT * SLOTS.length }}>
              {SLOTS.map((t, idx) => (
                <div
                  key={t}
                  style={{
                    position: "absolute",
                    top: idx * ROW_HEIGHT,
                    height: ROW_HEIGHT,
                    left: 0,
                    right: 0,
                    padding: "4px 8px",
                    color: "#fff",
                    fontSize: 12,
                  }}
                >
                  {t}
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      borderBottom: "1px solid #222",
                    }}
                  />
                </div>
              ))}
            </div>

            {/* Tages-Spalten */}
            {(viewMode === 'week' ? DAYS.map((_, i) => i) : [0]).map((dayIdx) => {
              const scopedEntries = viewScope === 'personal'
                ? entries.filter(e => e.userId === (selectedUserId ?? user?.id))
                : entries;
              const dayEvents = assignColumns(buildDayEvents(scopedEntries, weekStart, dayIdx));
              const dayHeight = ROW_HEIGHT * SLOTS.length;

              const dayDate = new Date(weekStart);
              dayDate.setHours(0, 0, 0, 0);
              dayDate.setDate(dayDate.getDate() + dayIdx);
              const today = new Date(now);
              today.setHours(0, 0, 0, 0);
              const showNowLine = isSameLocalDate(dayDate, today);
              const minutesNow = now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60;
              const nowTop = Math.max(0, Math.min(dayHeight - 1, minutesNow * PX_PER_MIN));

              return (
                <div
                  key={"col-" + dayIdx}
                  style={{
                    position: "relative",
                    height: dayHeight,
                    background:
                      "repeating-linear-gradient(to bottom, #1a1a1a, #1a1a1a 1px, #181818 1px, #181818 " +
                      ROW_HEIGHT +
                      "px)",
                    border: "1px solid #222",
                    borderRadius: 8,
                    overflow: "hidden",
                  }}
                  onDoubleClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const y = e.clientY - rect.top;
                    const minutes = Math.floor(y / PX_PER_MIN);
                    const startMin = Math.floor(minutes / 30) * 30;
                    const start = new Date(weekStart);
                    start.setHours(0, 0, 0, 0);
                    start.setDate(start.getDate() + dayIdx);
                    start.setMinutes(startMin);
                    openModal(dayIdx, startMin / 30, null);
                  }}
                >
                  {showNowLine && (
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: nowTop,
                        height: 2,
                        background: '#32CD89',
                        zIndex: 5,
                        pointerEvents: 'none',
                      }}
                      title={`Jetzt: ${now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                    />
                  )}
                  {/* Events rendern */}
                  {dayEvents.map((e) => {
                    const widthPct = 100 / e._clusterSize;
                    const leftPct = e._col * widthPct;

                    // Avatar-URL oder Default
                    const avatarUrl = resolveMediaUrl(e.avatarUrl && e.avatarUrl.length > 0 ? e.avatarUrl : '/avatars/default-avatar.png');
                    const detected = detectServiceFromUrl(e.streamLink);
                    const service = detected || detectServiceFallback(e.platform) || e.platform;
                    const platformStyle = PLATFORM_STYLES[service] || { color: '#32CD89', icon: '' };
                    const host = getHostname(e.streamLink);
                    const iconUrl = (service && platformIcons[service]) || (host && (platformIconsByHost[host] || platformIconsByHost[host?.replace(/^www\./,'')])) || null;

                    return (
                      <div
                        key={e.id}
                        onClick={() => openModal(dayIdx, Math.floor(e._startMin / 30), e)}
                        style={{
                          position: "absolute",
                          top: e._startMin * PX_PER_MIN,
                          left: `calc(${leftPct}% + ${GAP / 2}px)`,
                          width: `calc(${widthPct}% - ${GAP}px)`,
                          height: Math.max(18, e._durMin * PX_PER_MIN - GAP),
                          background: platformStyle.color,
                          color: "#222",
                          border: "1px solid #222",
                          borderRadius: 8,
                          padding: "6px 8px",
                          boxShadow: "0 2px 8px #0002",
                          cursor: "pointer",
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: 13,
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <img
                            src={avatarUrl}
                            alt="Avatar"
                            style={{
                              width: 22,
                              height: 22,
                              borderRadius: "50%",
                              objectFit: "cover",
                              marginRight: 4,
                              border: "1px solid #444",
                              background: "#222",
                            }}
                          />
                          {iconUrl ? (
                            <span
                              style={{
                                width: 20,
                                height: 20,
                                minWidth: 20,
                                minHeight: 20,
                                borderRadius: '50%',
                                background: '#fff',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 0 0 1px rgba(0,0,0,0.1)',
                                marginRight: 4,
                              }}
                              title={service}
                            >
                              <img src={iconUrl} alt={service} style={{ width: 14, height: 14, objectFit: 'contain' }} />
                            </span>
                          ) : (
                            <span style={{ fontSize: 16, marginRight: 4 }}>{platformStyle.icon}</span>
                          )}
              {e.title} <span style={{ fontSize: 11, opacity: 0.7 }}>({service || e.platform || 'Link'})</span>
                        </div>
                        {e.description && (
                          <div
                            style={{
                              fontSize: 11,
                              opacity: 0.85,
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                            }}
                          >
                            {e.description}
                          </div>
                        )}
                        <div style={{ fontSize: 10, opacity: 0.6, display: "flex", alignItems: "center", gap: 6 }}>
                          {new Date(e.start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} {" – "}
                          {new Date(e.end).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} {" · "}
                          <span>{e.userDisplayName || e.userId}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {/* Modal nur rendern, wenn gebraucht – mit key für sauberes Remounting */}
        {viewModal && (
          <EntryViewModal
            key={viewModal.entry?.id ?? 'view'}
            modal={viewModal}
            onClose={() => setViewModal(null)}
            onOpenProfile={(userId) => {
              setViewModal(null);
              navigate(`/u/${encodeURIComponent(userId)}`);
            }}
            platformIcons={platformIcons}
            platformIconsByHost={platformIconsByHost}
          />
        )}
        {modal && (
          <EntryModal
            key={modal.entry?.id ?? 'new'}
            modal={modal}
            weekStart={weekStart}
            user={user}
            onClose={() => setModal(null)}
            onSave={saveEntry}
            onDelete={deleteEntry}
            platformIcons={platformIcons}
            platformIconsByHost={platformIconsByHost}
          />
        )}
      </div>
    </div>
  );
}
